/* SPDX-License-Identifier: AGPL-3.0-or-later */

import { Component, Inject, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  TournamentEndpointService,
  TournamentQualificationMatchDto,
  TournamentQualificationMatchParticipantDto,
} from '@api';

export interface TournamentEditMatchResultsDialogData {
  tournamentId: number;
  matchId: number;
  participant1: TournamentQualificationMatchParticipantDto;
  participant2: TournamentQualificationMatchParticipantDto;
  previousResults: TournamentQualificationMatchDto | undefined;
}

@Component({
  selector: 'app-tournament-edit-match-results-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormField,
    MatFormFieldModule,
    MatIcon,
    MatInputModule,
    MatOption,
    MatSelect,
    ReactiveFormsModule,
  ],
  templateUrl: './tournament-edit-match-results-dialog.component.html',
  styleUrl: './tournament-edit-match-results-dialog.component.scss',
})
export class TournamentEditMatchResultsDialogComponent {
  tournamentService = inject(TournamentEndpointService);

  form = new FormGroup({
    winnerTeam: new FormControl<number | undefined>(undefined, [Validators.required]),
    winnerPoints: new FormControl<number | undefined>(undefined, [
      Validators.required,
      Validators.min(1),
      Validators.max(128),
    ]),
  });

  tournamentId: number;
  matchId: number;
  participant1: TournamentQualificationMatchParticipantDto;
  participant2: TournamentQualificationMatchParticipantDto;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    data: TournamentEditMatchResultsDialogData,
    private matDialogRef: MatDialogRef<TournamentEditMatchResultsDialogComponent>,
    private snackBar: MatSnackBar,
  ) {
    this.tournamentId = data.tournamentId;
    this.matchId = data.matchId;
    this.participant1 = data.participant1;
    this.participant2 = data.participant2;

    this.matDialogRef.disableClose = true;
    this.matDialogRef.backdropClick().subscribe(() => matDialogRef.close(null));

    if (data.previousResults) {
      const winnerId = data.previousResults.participants?.find(p => p.isWinner);

      this.form.controls.winnerTeam.setValue(winnerId?.teamId);
      this.form.controls.winnerPoints.setValue(data.previousResults.winnerPoints);
    }
  }

  updateQualificationMatchResults() {
    if (this.form.valid) {
      this.tournamentService
        .updateQualificationRound(this.tournamentId, this.matchId, {
          scoreUpdate: {
            winnerTeamId: +this.form.controls.winnerTeam.value!,
            winnerPoints: +this.form.controls.winnerPoints.value!,
          },
        })
        .subscribe({
          next: response => this.matDialogRef.close(response),
          error: err => {
            const errorMessage =
              typeof err.error === 'string' ? err.error : 'Failed to update match results';

            this.snackBar.open(errorMessage, 'Close', {
              duration: 5000,
            });
          },
        });
    } else {
      console.log('invalid form');
    }
  }

  compareTeams(a: number, b: number) {
    return +a === +b;
  }
}
